import React from 'react';
import * as yup from 'yup';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormGroup, Label, Input, Table, InputGroup, InputGroupText } from 'reactstrap';
import { InvoicingDto } from '../../../types';
import { Button } from '../../../components';
import { invoiceService } from '../../../services/InvoiceService';
import { generateYears } from '../../../common/Utils';

interface InvoiceFormProps {
    invoice: InvoicingDto;
    onSave: () => void;
    onCancel: () => void;
}

const InvoiceForm = (props: InvoiceFormProps) => {
    const schema = yup
        .object({
            id: yup.number().required().default(props.invoice.id),
            institutionId: yup.number().required().default(props.invoice.institutionId),
            institutionTitle: yup
                .string()
                .required()
                .default(props.invoice.institutionTitle || ''),
            status: yup.number().required().default(props.invoice.status),
            fiscalYear: yup
                .string()
                .notRequired()
                .default(props.invoice.fiscalYear || undefined),
            description: yup
                .string()
                .notRequired()
                .default(props.invoice.description || undefined),
            notificationEmail: yup
                .string()
                .notRequired()
                .default(props.invoice.description || undefined),
            amount: yup
                .number()
                .notRequired()
                .default(props.invoice.amount || undefined),
            tiers: yup
                .array()
                .of(
                    yup
                        .object({
                            numProfessions: yup.number().required().min(1),
                            perProfessionFee: yup.number().required().min(0),
                        })
                        .defined(),
                )
                .required(),
            invoiceDate: yup.string().required().default(moment(props.invoice.invoiceDate).format('YYYY-MM-DD')),
            automaticallySendOn: yup
                .string()
                .nullable()
                .notRequired()
                .default(props.invoice.automaticallySendOn || undefined),
            sendAutomatically: yup
                .boolean()
                .nullable()
                .notRequired()
                .default(props.invoice.sendAutomatically || undefined),
            createDate: yup
                .string()
                .notRequired()
                .default(props.invoice.createDate || undefined),
            lastUpdatedDate: yup
                .string()
                .nullable()
                .notRequired()
                .default(props.invoice.lastUpdatedDate || undefined),
            rowVersion: yup
                .string()
                .notRequired()
                .default(props.invoice.rowVersion || undefined),
            invoiceNumber: yup
                .string()
                .notRequired()
                .default(props.invoice.invoiceNumber || undefined),
            lateFee: yup
                .number()
                .min(0)
                .notRequired()
                .default(props.invoice.lateFee || 0),
            convenienceFee: yup
                .number()
                .min(0)
                .notRequired()
                .default(props.invoice.convenienceFee || 0),
            dueAmount: yup
                .number()
                .min(0)
                .notRequired()
                .default(props.invoice.dueAmount || undefined),
            shortUrl: yup.string().nullable().notRequired().default(props.invoice.shortUrl),
            programIds: yup.string().nullable().notRequired().default(props.invoice.programIds),
            programTitles: yup.string().nullable().notRequired().default(props.invoice.programTitles),
            professionList: yup
                .string()
                .nullable()
                .notRequired()
                .default(JSON.stringify(props.invoice.professionListDeserialized)),
            isDeleted: yup.boolean().required().default(props.invoice.isDeleted),
            invoiceType: yup.number().required().default(props.invoice.invoiceType),
            recipientId: yup.number().nullable().notRequired().default(props.invoice.recipientId),
            stripeInvoiceUrl: yup.string().nullable().notRequired().default(props.invoice.stripeInvoiceUrl),
            pdfUrl: yup.string().nullable().notRequired().default(props.invoice.pdfUrl),
            dueDate: yup.string().nullable().notRequired().default(props.invoice.dueDate),
        })
        .defined();

    const {
        handleSubmit,
        register,
        control,
        watch,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(schema as any),
        defaultValues: {
            ...props.invoice,
            description: props.invoice.id === 0 ? 'CAAHEP Accredited Program Fee' : props.invoice.description,
            invoiceDate: moment(props.invoice.invoiceDate).format('YYYY-MM-DD'),
            lateFee: props.invoice.lateFee || 0,
            tiers: [
                {
                    numProfessions: 1,
                    perProfessionFee: 660,
                },
                {
                    numProfessions: 2,
                    perProfessionFee: 550,
                },
                {
                    numProfessions: 3,
                    perProfessionFee: 385,
                },
                {
                    numProfessions: 4,
                    perProfessionFee: 275,
                },
                {
                    numProfessions: 5,
                    perProfessionFee: 220,
                },
            ],
        },
    });
    const {
        fields,
        append,
        remove: removeTier,
    } = useFieldArray({
        name: 'tiers',
        control,
    });
    const formValues = watch();

    const addTier = () => {
        append({ numProfessions: 1, perProfessionFee: 0 });
    };

    const onSubmit = (values: any) => {
        const toastId = toast.info('Saving Invoice...', {
            closeOnClick: false,
            draggable: false,
            autoClose: false,
        });
        invoiceService
            .saveInvoice(values.id, values)
            .then((savedInvoice) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Invoice saved successfully',
                    autoClose: 3000,
                    closeOnClick: true,
                    draggable: true,
                });

                if (values.sendAutomatically) {
                    const invoiceId = savedInvoice?.id || values.id;
                    const emailToastId = toast.info('Emailing Invoice...');
                    invoiceService
                        .emailInvoices([invoiceId])
                        .then(() => {
                            toast.update(emailToastId, {
                                type: 'success',
                                render: 'Invoice emailed successfully',
                            });
                        })
                        .catch(() =>
                            toast.update(emailToastId, {
                                type: 'error',
                                render: 'Error emailing invoice',
                            }),
                        );
                }

                props.onSave();
            })
            .catch((e) => {
                if (e.error) {
                    toast.update(toastId, {
                        type: 'error',
                        render: e.error,
                        autoClose: 5000,
                        closeOnClick: true,
                        draggable: true,
                    });
                } else {
                    toast.update(toastId, {
                        type: 'error',
                        render: 'Error saving Invoice',
                        autoClose: 5000,
                        closeOnClick: true,
                        draggable: true,
                    });
                }
            });
    };

    return (
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
                <Label for="invoiceDate">Invoice Date</Label>
                <Controller
                    name="invoiceDate"
                    control={control}
                    render={({ field }) => (
                        <Input
                            type="date"
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value)}
                        />
                    )}
                />
                {errors.invoiceDate && <span className="text-danger">{errors.invoiceDate.message}</span>}
            </FormGroup>
            <FormGroup>
                <Label for="description">Description</Label>
                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <Input
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value || null)}
                        />
                    )}
                />
                {errors.description && <span className="text-danger">{errors.description.message}</span>}
            </FormGroup>
            {!props.invoice.fiscalYear && (
                <FormGroup>
                    <Label for="fiscalYear">Fiscal Year</Label>
                    <Controller
                        name="fiscalYear"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="select"
                                {...field}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value || null)}
                            >
                                <option value={''}>Select...</option>
                                {generateYears().map((ct) => (
                                    <option key={ct} value={ct}>
                                        {ct}
                                    </option>
                                ))}
                            </Input>
                        )}
                    />
                    {errors.fiscalYear && <span className="text-danger">{errors.fiscalYear.message}</span>}
                </FormGroup>
            )}
            {(!props.invoice.amount || props.invoice.status !== 1) && (
                <FormGroup>
                    <Label for="amount">Accredited Program Fee Structure</Label>
                    <div>
                        <Table>
                            <thead>
                                <tr>
                                    <th>{`# Programs at Institution`}</th>
                                    <th>{`Per Program Fee`}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map((field, i) => (
                                    <tr key={field.id}>
                                        <td>{i + 1}</td>
                                        <td>
                                            <InputGroup>
                                                <InputGroupText>$</InputGroupText>
                                                <Controller
                                                    name={`tiers.${i}.perProfessionFee`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) =>
                                                                field.onChange(
                                                                    e.target.value ? Number(e.target.value) : 0,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`tiers.${i}.numProfessions`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input type="hidden" {...field} value={i + 1} />
                                                    )}
                                                />
                                            </InputGroup>
                                            {errors.tiers?.[i]?.perProfessionFee && (
                                                <span className="text-danger small">
                                                    {errors.tiers[i]?.perProfessionFee?.message}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <Button
                                                color="danger"
                                                type="button"
                                                onClick={() => removeTier(i)}
                                                disabled={fields.length <= 1}
                                            >
                                                <i className="mdi mdi-close" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    <td colSpan={3}>
                                        <Button type="button" color="primary" onClick={addTier}>
                                            <i className="mdi mdi-plus" />
                                            <span className="ml-2">Add a Fee Tier</span>
                                        </Button>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                </FormGroup>
            )}
            <FormGroup>
                <Label for="lateFee">Administrative Fee</Label>
                <Controller
                    name="lateFee"
                    control={control}
                    render={({ field }) => (
                        <Input
                            type="number"
                            step="0.01"
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                        />
                    )}
                />
                {errors.lateFee && <span className="text-danger">{errors.lateFee.message}</span>}
            </FormGroup>

            <FormGroup>
                <Label check>
                    Send Now?&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <Controller
                        name="sendAutomatically"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="checkbox"
                                name={field.name}
                                onBlur={field.onBlur}
                                ref={field.ref}
                                checked={field.value || false}
                                onChange={(e) => field.onChange(e.target.checked)}
                            />
                        )}
                    />
                </Label>
            </FormGroup>

            <div>
                <Button type="button" color="link" onClick={props.onCancel}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} color="primary">
                    {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
            </div>
        </Form>
    );
};

export default InvoiceForm;
